import { differenceInCalendarDays, endOfDay, isValid, startOfDay, startOfMonth, subDays } from 'date-fns';

export type PeriodPreset = 'current_month' | 'last_30' | 'last_90' | 'last_180' | 'custom';

export const MAX_CUSTOM_PERIOD_DAYS = 365;
export const DATE_PICKER_MAX_RANGE_DAYS = MAX_CUSTOM_PERIOD_DAYS - 1;

export const PRESET_OPTIONS: { value: PeriodPreset; label: string }[] = [
  { value: 'current_month', label: 'Mês atual' },
  { value: 'last_30', label: 'Últimos 30 dias' },
  { value: 'last_90', label: '3 meses' },
  { value: 'last_180', label: '6 meses' },
  { value: 'custom', label: 'Personalizado' },
];

export interface PeriodSelection {
  preset: PeriodPreset;
  from: Date;
  to: Date;
}

const DATE_PARAM_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

function parseDateParam(value?: string | null): Date | null {
  if (!value) return null;
  const trimmed = value.trim();
  const match = DATE_PARAM_PATTERN.exec(trimmed);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const parsed = new Date(year, month - 1, day);

  if (
    !isValid(parsed) ||
    parsed.getFullYear() !== year ||
    parsed.getMonth() !== month - 1 ||
    parsed.getDate() !== day
  ) {
    return null;
  }

  return startOfDay(parsed);
}

function clampToToday(date: Date, today: Date): Date {
  return date > today ? today : date;
}

export function countInclusivePeriodDays(from: Date, to: Date): number {
  return Math.abs(differenceInCalendarDays(to, from)) + 1;
}

export function normalizeCustomPeriodRange(
  customFrom?: string | null,
  customTo?: string | null,
  now = new Date(),
): { from: Date; to: Date } {
  const todayStart = startOfDay(now);
  const todayEnd = endOfDay(now);

  let from = clampToToday(parseDateParam(customFrom) ?? startOfMonth(now), todayStart);
  let to = clampToToday(parseDateParam(customTo) ?? todayStart, todayStart);

  if (to < from) {
    [from, to] = [to, from];
  }

  if (countInclusivePeriodDays(from, to) > MAX_CUSTOM_PERIOD_DAYS) {
    from = subDays(to, MAX_CUSTOM_PERIOD_DAYS - 1);
  }

  return {
    from,
    to: to.getTime() === todayStart.getTime() ? todayEnd : endOfDay(to),
  };
}

// Used by period-scoped screens. Caps at endOfDay so client-side filtering includes
// today's rows. Transactions has its own variant (caps at instant `now`)
// kept inline in transactions/layout.tsx because the URL flow needs it.
export function computePeriodDateRange(
  preset: PeriodPreset,
  customFrom?: string | null,
  customTo?: string | null,
  now = new Date(),
): { from: Date; to: Date } {
  const todayEnd = endOfDay(now);
  switch (preset) {
    case 'current_month':
      return { from: startOfMonth(now), to: todayEnd };
    case 'last_30':
      return { from: subDays(now, 30), to: todayEnd };
    case 'last_90':
      return { from: subDays(now, 90), to: todayEnd };
    case 'last_180':
      return { from: subDays(now, 180), to: todayEnd };
    case 'custom': {
      return normalizeCustomPeriodRange(customFrom, customTo, now);
    }
  }
}

export function computeBudgetMonthsForPeriod(selection: Pick<PeriodSelection, 'preset' | 'from' | 'to'>): number {
  switch (selection.preset) {
    case 'current_month':
    case 'last_30':
      return 1;
    case 'last_90':
      return 3;
    case 'last_180':
      return 6;
    case 'custom': {
      const inclusiveDays = countInclusivePeriodDays(selection.from, selection.to);
      return Math.max(1 / 30, inclusiveDays / 30);
    }
  }
}
